import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import {
  Play,
  Square,
  Pause,
  RefreshCw,
  TrendingUp,
  Activity,
  DollarSign,
  Settings,
  BarChart3,
  Clock,
  Target,
  AlertTriangle,
  Download,
  Sliders,
  Shield,
  Calendar,
  Mail,
  Volume2,
  VolumeX,
  Calculator,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Area,
  AreaChart,
} from 'recharts';
import { Toaster, toast } from 'sonner';
import SettingsModal from './SettingsModal';
import PerformanceDashboard from './PerformanceDashboard';
import SystemMessageBanner from './SystemMessageBanner';
import SystemMessageModal from './SystemMessageModal';
import { useAuth } from './AuthContext';
import CandlestickChart from './CandlestickChart';
import CoinOverviewTable from './CoinOverviewTable';
import BotDiagnostics from './BotDiagnostics';
import ManualTradeModal from './ManualTradeModal';
import ManualBuyLotsSection from './ManualBuyLotsSection';
import PriceCompare from './PriceCompare';
import ArbitrageScanner from './ArbitrageScanner';
import MiniPlayer from './MiniPlayer';
import MultiCoinOverlay from './MultiCoinOverlay';
import { CostBasisModal } from './CostBasisModal';
import { TradeHistoryTab } from './TradeHistoryTab';
// Import new dashboard components
import LiveTradeFeed from './dashboard/LiveTradeFeed';
import ProtectionSellAudit from './dashboard/ProtectionSellAudit';
import RSICountdown from './dashboard/RSICountdown';
import { DashboardHeader, StatsRow, MultiCoinStatus, PortfolioDetails, BotControlsCard, SettingsDisplay, GridTradingCard } from './dashboard';
import { ChartTooltip, getRsiColor, getRsiStatus, getEnhancedChartData, getSelectedChartData as getSelectedChartDataHelper } from './dashboard/chartHelpers';
import { createBotControls } from './dashboard/botControls';
import { formatCountdown, formatElapsedTime, formatCurrency, formatPrice } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const API_V2 = `${BACKEND_URL}/api/v2`;  // New modular API endpoints
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

const Dashboard = () => {
  // Auth & Navigation
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Refs for toast queue
  const toastQueueRef = useRef([]);
  
  // State
  const [botStatus, setBotStatus] = useState('stopped');
  const [tradingMode, setTradingMode] = useState('simulation');
  const [accessLevel, setAccessLevel] = useState('FREE');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [rsi, setRsi] = useState(50);
  const [signal, setSignal] = useState('HOLD');
  const [audioAlertsEnabled, setAudioAlertsEnabled] = useState(true);
  const [lastMilestone, setLastMilestone] = useState(0);
  const audioRef = useRef(null);
  const [portfolio, setPortfolio] = useState({
    usd_balance: 10000,
    btc_balance: 0,
    eth_balance: 0,
    sol_balance: 0,
    ltc_balance: 0,
    doge_balance: 0,
    bch_balance: 0,
    xrp_balance: 0,
    shib_balance: 0,
    dgb_balance: 0,
    total_value: 10000,
    avg_buy_price: 0,
    pnl: 0,
    pnl_percent: 0,
    total_trades: 0,
    winning_trades: 0,
    win_rate: 0,
  });
  const [priceHistory, setPriceHistory] = useState([]);
  const [ethPriceHistory, setEthPriceHistory] = useState([]);
  const [solPriceHistory, setSolPriceHistory] = useState([]);
  const [ltcPriceHistory, setLtcPriceHistory] = useState([]);
  const [dogePriceHistory, setDogePriceHistory] = useState([]);
  const [bchPriceHistory, setBchPriceHistory] = useState([]);
  const [xrpPriceHistory, setXrpPriceHistory] = useState([]);
  const [shibPriceHistory, setShibPriceHistory] = useState([]);
  const [dgbPriceHistory, setDgbPriceHistory] = useState([]);
  const [selectedChart, setSelectedChart] = useState('BTC-USD');
  const [chartTimeRange, setChartTimeRange] = useState(43200); // Time range in minutes (default 30 days)
  const [trades, setTrades] = useState([]);
  const [lastTradeRefresh, setLastTradeRefresh] = useState(null);
  const [tickCounter, setTickCounter] = useState(0); // Forces re-render for "updated Xs ago"
  const [isConnected, setIsConnected] = useState(false);
  const [settings, setSettings] = useState({
    rsi_oversold: 30,
    rsi_overbought: 70,
    trade_amount: 100,
    trade_percent: 10,
    use_percent: true,
    min_trade: 1,
    active_pairs: ['BTC-USD'],
    supported_pairs: ['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'],
  });
  const [coinbaseConnected, setCoinbaseConnected] = useState(false);
  const [coinbaseBalance, setCoinbaseBalance] = useState(null);
  const [liveDataSource, setLiveDataSource] = useState(null); // Track where LIVE data comes from
  const [activeExchange, setActiveExchange] = useState(null);
  const [lifetimeProfit, setLifetimeProfit] = useState(0);
  const [lifetimeProfitResetDate, setLifetimeProfitResetDate] = useState('2026-01-22');
  const [lifetimeSkim, setLifetimeSkim] = useState(0);
  const [skimData, setSkimData] = useState({ session_profit: 0, session_skim: 0, next_skim_at: 500, skim_trigger_amount: 500, skim_transfer_amount: 5 });
  const [gridData, setGridData] = useState({ enabled: false, base_price: 0, size: 100, next_buy: 0, next_sell: 0, buy_levels: [], sell_levels: [], pending_buys: 0, pending_sells: 0 });
  const [pairsData, setPairsData] = useState({});  // Multi-pair data
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualTradeOpen, setManualTradeOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('trading');
  const [activeUsersCount, setActiveUsersCount] = useState(null); // v3.9.30: null = unloaded (shows "…"), never a spurious "0 USERS" on first paint
  const [isAdmin, setIsAdmin] = useState(false);
  const [systemMessages, setSystemMessages] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [fullSyncStatus, setFullSyncStatus] = useState(null);
  const [fullSyncing, setFullSyncing] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);  // Stop bot confirmation modal
  
  // Helper function to format prices correctly for different coins
  const formatPrice = (price, pair) => {
    if (!price || price === 0) return '0.00';
    
    // For very small prices (like SHIB, DOGE), show more decimal places
    if (price < 0.0001) {
      return price.toFixed(8);
    } else if (price < 0.01) {
      return price.toFixed(6);
    } else if (price < 1) {
      return price.toFixed(4);
    } else {
      return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };
  
  // Sync portfolio with exchange
  const syncWithExchange = async () => {
    if (tradingMode !== 'live') {
      toast.error('Sync only available in LIVE mode');
      return;
    }
    setSyncing(true);
    try {
      const response = await axios.post(`${API_V2}/user/bot/sync-exchange`, {}, { withCredentials: true });
      console.log('[SYNC RESPONSE]', response.data);
      if (response.data.success) {
        const debugInfo = response.data.debug || {};
        let desc = `Total: $${response.data.old_total} → $${response.data.new_total}`;
        if (response.data.orders_synced) {
          desc += `, ${response.data.orders_synced} orders imported`;
        }
        // Show avg buy prices in console for debugging
        if (debugInfo.avg_buy_prices) {
          console.log('[SYNC] Avg buy prices:', debugInfo.avg_buy_prices);
        }
        if (debugInfo.trade_counts_in_db) {
          console.log('[SYNC] Trade counts in DB:', debugInfo.trade_counts_in_db);
        }
        toast.success('Portfolio synced with exchange', { description: desc });
        // Refresh all data
        fetchCoinbaseBalance();
        fetchBotStatus();
        fetchTrades();
      } else {
        toast.error('Sync failed', { description: response.data.error });
      }
    } catch (error) {
      console.error('Sync failed:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Failed to sync with exchange', { description: errorMsg });
    } finally {
      setSyncing(false);
    }
  };
  
  // Full history sync (background task - fetches ALL orders)
  const startFullHistorySync = async () => {
    if (tradingMode !== 'live') {
      toast.error('Full sync only available in LIVE mode');
      return;
    }
    setFullSyncing(true);
    try {
      const response = await axios.post(`${API_V2}/sync/start-full-history`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success('Full history sync started', { description: 'This may take a few minutes for large accounts.' });
        // Start polling for status
        pollFullSyncStatus();
      } else {
        toast.error(response.data.message || 'Failed to start sync');
        setFullSyncing(false);
      }
    } catch (error) {
      console.error('Full sync failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error('Failed to start full sync', { description: errorMsg });
      setFullSyncing(false);
    }
  };
  
  const pollFullSyncStatus = async () => {
    try {
      const response = await axios.get(`${API_V2}/sync/status`, { withCredentials: true });
      setFullSyncStatus(response.data);
      
      if (response.data.status === 'running' || response.data.status === 'starting') {
        // Continue polling every 2 seconds
        setTimeout(pollFullSyncStatus, 2000);
      } else if (response.data.status === 'completed') {
        toast.success('Full history sync completed!', { 
          description: `${response.data.orders_synced} orders synced from exchange.` 
        });
        setFullSyncing(false);
        // Refresh data - use calc_pl=true after sync to compute missing P/L
        fetchCoinbaseBalance();
        fetchBotStatus();
        axios.get(`${API_V2}/user/trades?limit=100&calc_pl=true`, { withCredentials: true })
          .then(res => { setTrades(res.data.trades || []); setLastTradeRefresh(new Date()); })
          .catch(() => fetchTrades());
      } else if (response.data.status === 'error') {
        toast.error('Sync failed', { description: response.data.error });
        setFullSyncing(false);
      } else {
        setFullSyncing(false);
      }
    } catch (error) {
      console.error('Error polling sync status:', error);
      setFullSyncing(false);
    }
  };
  
  // Dump coin confirmation state
  const [dumpConfirm, setDumpConfirm] = useState({ open: false, pair: null, coinName: null });
  
  // Bleed mode state (sell-only for specific coins)
  const [bleedPairs, setBleedPairs] = useState([]);
  const [bleedLoading, setBleedLoading] = useState(null);
  
  // Cost Basis Debug modal state
  const [costBasisOpen, setCostBasisOpen] = useState(false);
  
  // Set Avg Buy Price dialog state
  const [setAvgBuyDialog, setSetAvgBuyDialog] = useState({ open: false, pair: null, coinName: null, currentBalance: 0 });
  const [avgBuyInput, setAvgBuyInput] = useState('');
  
  const handleSetAvgBuy = async () => {
    if (!setAvgBuyDialog.pair || !avgBuyInput || !user) return;
    
    const avgPrice = parseFloat(avgBuyInput);
    if (isNaN(avgPrice) || avgPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_V2}/debug/user/${user.google_id}/set-avg-buy/${setAvgBuyDialog.pair}`,
        null,
        { 
          params: { avg_price: avgPrice, amount: setAvgBuyDialog.currentBalance },
          withCredentials: true 
        }
      );
      
      if (response.data.success) {
        toast.success('Average buy price set', {
          description: `${setAvgBuyDialog.coinName}: $${avgPrice}`
        });
        setSetAvgBuyDialog({ open: false, pair: null, coinName: null, currentBalance: 0 });
        setAvgBuyInput('');
        fetchBotStatus(); // Refresh to show new avg buy
      } else {
        toast.error('Failed to set avg buy', { description: response.data.error });
      }
    } catch (error) {
      console.error('Set avg buy failed:', error);
      toast.error('Failed to set avg buy price');
    }
  };
  
  const handleOpenCostBasis = () => {
    setCostBasisOpen(true);
  };
  
  const [runTimer, setRunTimer] = useState({
    startTime: null,
    elapsedSeconds: 0,
    startingBalance: 0,
    currentProfit: 0,
    profitPerHour: 0,
    isLoaded: false,  // Track if timer data has been loaded from backend
  });
  
  // RSI Stabilization countdown (30 seconds)
  // Initialize from localStorage to persist across navigation
  const [rsiCountdown, setRsiCountdown] = useState(() => {
    try {
      const stored = localStorage.getItem('rsiCountdownState');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only restore if it's for the same session (check sessionStartTime)
        if (parsed.sessionStartTime && parsed.hasCompleted) {
          return {
            isActive: false,
            secondsRemaining: 0,
            sessionStartTime: parsed.sessionStartTime,
            hasCompleted: true,
          };
        }
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return {
      isActive: false,
      secondsRemaining: 30,
      sessionStartTime: null,
      hasCompleted: false,
    };
  });

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const connectWebSocketRef = useRef(null);
  const fetchTradesRef = useRef(null);
  const showTradeToastRef = useRef(null);
  const countdownMusicRef = useRef(null);
  const musicPlayingRef = useRef(false);

  // Fetch initial data - uses v2 user-specific endpoint
  const fetchBotStatus = useCallback(async () => {
    if (!user) return; // Require authentication
    try {
      const response = await axios.get(`${API_V2}/user/bot/status`, {
        withCredentials: true
      });
      const data = response.data;
      
      console.log('fetchBotStatus received data.active_pairs:', data.active_pairs);
      
      // Only update botStatus if it actually changed to avoid triggering effects unnecessarily
      setBotStatus(prev => prev !== data.status ? data.status : prev);
      console.log('[BOT STATUS] Mode from server:', data.mode);
      setTradingMode(data.mode);
      if (data.access_level) setAccessLevel(data.access_level);
      setCurrentPrice(data.current_price);
      setRsi(data.rsi);
      setSignal(data.last_signal);
      setPortfolio(data.portfolio);
      
      const newSettings = {
        rsi_oversold: data.rsi_oversold,
        rsi_overbought: data.rsi_overbought,
        trade_amount: data.trade_amount,
        trade_percent: data.trade_percent || 10,
        use_percent: data.use_percent !== false,
        min_trade: data.min_trade || 1,
        // Protection features
        stop_loss_enabled: data.stop_loss_enabled || false,
        stop_loss_percent: data.stop_loss_percent || 5,
        take_profit_enabled: data.take_profit_enabled || false,
        take_profit_percent: data.take_profit_percent || 10,
        trailing_stop_enabled: data.trailing_stop_enabled || false,
        trailing_stop_percent: data.trailing_stop_percent || 3,
        // Schedule
        schedule_enabled: data.schedule_enabled || false,
        schedule_start_hour: data.schedule_start_hour || 9,
        schedule_end_hour: data.schedule_end_hour || 17,
        schedule_days: data.schedule_days || [0, 1, 2, 3, 4],
        // Email/notifications
        email_alerts_enabled: data.email_alerts_enabled || false,
        alert_email: data.alert_email || '',
        alert_on_trade: data.alert_on_trade !== false,
        alert_on_error: data.alert_on_error !== false,
        push_enabled: data.push_enabled || false,
        // Pairs
        active_pairs: data.active_pairs || ['BTC-USD'],
        supported_pairs: data.supported_pairs || ['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'],
      };
      
      console.log('Setting settings with active_pairs:', newSettings.active_pairs);
      setSettings(newSettings);
      setCoinbaseConnected(data.coinbase_connected || false);
      
      // Update bleed pairs from status response
      if (data.bleed_pairs) {
        setBleedPairs(data.bleed_pairs);
      }
      
      // Fetch active exchange info
      try {
        const exchangeRes = await axios.get(`${API_V2}/exchanges/active`, { withCredentials: true });
        setActiveExchange(exchangeRes.data);
      } catch (err) {
        console.log('No exchange configured:', err);
      }
      
      // Update pairs data - preserve avg_buy_price to prevent flicker
      if (data.pairs) {
        setPairsData(prevPairs => {
          const newPairs = { ...data.pairs };
          // For each pair, keep old avg_buy_price if:
          // 1. New one is 0 but old one exists
          // 2. Old one exists and new one is within 1% (likely same value with rounding)
          Object.keys(newPairs).forEach(pair => {
            const oldAvg = prevPairs[pair]?.avg_buy_price || 0;
            const newAvg = newPairs[pair].avg_buy_price || 0;
            
            if (oldAvg > 0) {
              if (newAvg === 0) {
                // Keep old if new is 0
                newPairs[pair].avg_buy_price = oldAvg;
              } else if (Math.abs(newAvg - oldAvg) / oldAvg < 0.01) {
                // Keep old if within 1% to prevent micro-flickering
                newPairs[pair].avg_buy_price = oldAvg;
              }
            }
          });
          return newPairs;
        });
      }
      
      // Update session timer from backend - do this FIRST before botStatus triggers timer effect
      if (data.session_timer) {
        const timer = data.session_timer;
        const startTimeMs = timer.start_time ? new Date(timer.start_time).getTime() : null;
        
        // Only update runTimer if:
        // 1. It's the first load (no startTime yet), OR
        // 2. The session start time changed (new session)
        // This prevents jumping when local timer is running
        setRunTimer(prev => {
          // If local timer is already running for this session, only update profit/balance info
          if (prev.isLoaded && prev.startTime === startTimeMs && botStatus === 'running') {
            // Keep local elapsedSeconds, just sync profit data from backend for accuracy
            return {
              ...prev,
              startingBalance: timer.start_balance || prev.startingBalance,
              // Don't override elapsedSeconds - local timer handles this
            };
          }
          
          // New session or first load - use backend values
          return {
            startTime: startTimeMs,
            elapsedSeconds: timer.elapsed_seconds || 0,
            startingBalance: timer.start_balance || 0,
            currentProfit: timer.session_profit ?? 0,
            profitPerHour: timer.profit_per_hour ?? 0,
            isLoaded: true,
          };
        });
        
        // Check if this is a NEW session (started within last 30 seconds)
        const RSI_STABILIZATION_TIME = 30; // 30 seconds
        if (data.status === 'running' && timer.elapsed_seconds !== undefined && startTimeMs) {
          // Calculate elapsed from start_time locally (more reliable than backend elapsed_seconds
          // which can briefly be 0 after server restarts)
          const elapsedSeconds = startTimeMs 
            ? Math.floor((Date.now() - startTimeMs) / 1000)
            : timer.elapsed_seconds;
          
          // Check localStorage for persisted completion state
          let storedCompleted = false;
          try {
            const stored = localStorage.getItem('rsiCountdownState');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed.sessionStartTime === startTimeMs && parsed.hasCompleted) {
                storedCompleted = true;
              }
            }
          } catch (e) {}
          
          setRsiCountdown(prev => {
            // RULE 0: If localStorage says completed for this session, respect it
            if (storedCompleted) {
              return { isActive: false, secondsRemaining: 0, sessionStartTime: startTimeMs, hasCompleted: true };
            }
            
            // RULE 1: If backend says session is past 30 seconds, NEVER show countdown
            // This handles page remounts and ensures mature sessions never see the timer
            if (elapsedSeconds >= RSI_STABILIZATION_TIME) {
              return { isActive: false, secondsRemaining: 0, sessionStartTime: startTimeMs, hasCompleted: true };
            }
            
            // RULE 2: If countdown already completed for THIS session, don't restart
            if (prev.hasCompleted && prev.sessionStartTime === startTimeMs) {
              return prev;
            }
            
            // RULE 3: If countdown is currently running, let it continue (don't reset from backend)
            if (prev.isActive && prev.secondsRemaining > 0) {
              return prev;
            }
            
            // RULE 4: New session (< 240 seconds) - only start countdown if not already completed
            // This is the ONLY path where countdown can be activated
            const remaining = Math.max(0, Math.ceil(RSI_STABILIZATION_TIME - elapsedSeconds));
            if (remaining > 0 && !prev.hasCompleted) {
              return {
                isActive: true,
                secondsRemaining: remaining,
                sessionStartTime: startTimeMs,
                hasCompleted: false,
              };
            }
            
            return prev;
          });
        } else if (data.status === 'stopped') {
          // Bot is stopped, reset countdown state completely for next session
          setRsiCountdown(prev => {
            if (prev.isActive || prev.sessionStartTime !== null || prev.hasCompleted) {
              return { isActive: false, secondsRemaining: 30, sessionStartTime: null, hasCompleted: false };
            }
            return prev;
          });
        }
      } else {
        // No session timer from backend - mark as loaded but keep zeros
        setRunTimer(prev => ({ ...prev, isLoaded: true }));
      }
    } catch (error) {
      console.error('Failed to fetch bot status:', error);
    }
  }, [user]);

  // Web Audio API - generates tones directly, no external files needed
  const audioContextRef = useRef(null);
  const sellAudioRef = useRef(null);
  
  // Lazy-load the cash-register mp3 once
  const getSellAudio = useCallback(() => {
    if (!sellAudioRef.current) {
      try {
        const a = new Audio('/sell-sound.mp3');
        a.preload = 'auto';
        a.volume = 0.85;
        sellAudioRef.current = a;
      } catch {
        sellAudioRef.current = null;
      }
    }
    return sellAudioRef.current;
  }, []);
  
  const getAudioContext = useCallback(() => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      // Always try to resume - Safari suspends aggressively
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      return audioContextRef.current;
    } catch {
      return null;
    }
  }, []);

  const playAlertSound = useCallback((type = 'trade') => {
    if (!audioAlertsEnabled) return;
    try {
      let ctx = audioContextRef.current;
      if (!ctx || ctx.state === 'closed') {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;
      }
      
      const doPlay = () => {
        const now = ctx.currentTime;
        if (type === 'buy' || type === 'trade') {
        // Pronounced two-tone beep for BUY — low then one octave up
        const freqs = [440, 880]; // A4 -> A5 (one octave up)
        freqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'square';
          gain.gain.setValueAtTime(0.315, now + i * 0.2);
          gain.gain.setValueAtTime(0.315, now + i * 0.2 + 0.12);
          gain.gain.exponentialRampToValueAtTime(0.007, now + i * 0.2 + 0.18);
          osc.start(now + i * 0.2);
          osc.stop(now + i * 0.2 + 0.19);
        });
      } else if (type === 'sell') {
        // NEW (v3.9.8): Play the user-uploaded cash-register MP3 instead of the synthesized version.
        // Fall back to the synthesized cha-ching if the audio element fails (offline, blocked, etc).
        const playSynthFallback = () => {
        // Cash register CHA-CHING!
        // Part 1: "CHA" — mechanical drawer slam (noise burst + low thud)
        const bufLen = ctx.sampleRate * 0.06;
        const noiseBuf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
        const noiseData = noiseBuf.getChannelData(0);
        for (let i = 0; i < bufLen; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.6;
        const noiseSrc = ctx.createBufferSource();
        noiseSrc.buffer = noiseBuf;
        const noiseGain = ctx.createGain();
        const noiseFilter = ctx.createBiquadFilter();
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.value = 2000;
        noiseFilter.Q.value = 1.5;
        noiseSrc.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(ctx.destination);
        noiseGain.gain.setValueAtTime(0.35, now);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
        noiseSrc.start(now);
        noiseSrc.stop(now + 0.07);

        // Part 2: "CHING!" — bright bell ring at 0.08s
        const bellTime = now + 0.08;
        // Two detuned sines for metallic shimmer
        [2093, 2637].forEach((freq) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.connect(g);
          g.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          g.gain.setValueAtTime(0.30, bellTime);
          g.gain.setTargetAtTime(0.0, bellTime + 0.05, 0.18);
          osc.start(bellTime);
          osc.stop(bellTime + 0.7);
        });
        // Bright harmonic for sparkle
        const sparkOsc = ctx.createOscillator();
        const sparkG = ctx.createGain();
        sparkOsc.connect(sparkG);
        sparkG.connect(ctx.destination);
        sparkOsc.frequency.value = 4186;
        sparkOsc.type = 'sine';
        sparkG.gain.setValueAtTime(0.12, bellTime);
        sparkG.gain.setTargetAtTime(0.0, bellTime + 0.03, 0.12);
        sparkOsc.start(bellTime);
        sparkOsc.stop(bellTime + 0.5);

        // Part 3: Coin jingle tail — rapid descending sparkle
        [3520, 3135, 2793, 3520].forEach((freq, i) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.frequency.value = freq;
          o.type = 'sine';
          const t = bellTime + 0.20 + i * 0.06;
          g.gain.setValueAtTime(0.10, t);
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.10);
          o.start(t);
          o.stop(t + 0.11);
        });
        };
        
        const audio = getSellAudio();
        if (audio) {
          try {
            audio.currentTime = 0;
            const p = audio.play();
            if (p && typeof p.catch === 'function') {
              p.catch((err) => {
                console.warn('[SOUND] sell mp3 play failed, falling back to synth:', err?.message || err);
                playSynthFallback();
              });
            }
          } catch (e) {
            console.warn('[SOUND] sell mp3 threw, falling back to synth:', e?.message || e);
            playSynthFallback();
          }
        } else {
          playSynthFallback();
        }
      } else if (type === 'milestone') {
        // Triumphant rising arpeggio for milestone (C5 E5 G5 C6)
        [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.25, now + i * 0.12);
          gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.2);
          osc.start(now + i * 0.12);
          osc.stop(now + i * 0.12 + 0.25);
        });
      }
      };
      
      if (ctx.state === 'running') {
        doPlay();
      } else if (ctx.state === 'suspended') {
        // Try to resume - will work if called during/after a user gesture
        ctx.resume().then(() => {
          if (ctx.state === 'running') doPlay();
        }).catch(() => {});
      }
    } catch (e) {
      console.warn('[SOUND] Audio playback failed:', e.message);
    }
  }, [audioAlertsEnabled, getSellAudio]);

  // Keep AudioContext alive on EVERY user interaction (Safari suspends aggressively)
  // Also eagerly create the context on first click so it's ready for WS trade sounds
  useEffect(() => {
    const unlock = () => {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        try {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) { return; }
      }
      const ctx = audioContextRef.current;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => {
          console.log('[SOUND] AudioContext unlocked and running');
        }).catch(() => {});
      }
      // Also unlock the sell-sound mp3 (mobile autoplay policies require a gesture)
      try {
        const a = getSellAudio();
        if (a && a.readyState === 0) {
          a.load();
        }
      } catch { /* ignore */ }
    };
    document.addEventListener('click', unlock);
    document.addEventListener('touchstart', unlock);
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  const toggleAudioAlerts = useCallback(() => {
    setAudioAlertsEnabled(prev => {
      const newState = !prev;
      toast.success(newState ? 'Audio alerts ON' : 'Audio alerts OFF', {
        id: 'audio-toggle-notification',
        duration: 2000,
      });
      if (newState) {
        // Force create & resume context on user gesture (most reliable moment)
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioContextRef.current = ctx;
          ctx.resume().then(() => {
            // Play test buy beep - low then octave up
            const now = ctx.currentTime;
            [440, 880].forEach((freq, i) => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = freq;
              osc.type = 'square';
              gain.gain.setValueAtTime(0.45, now + i * 0.2);
              gain.gain.setValueAtTime(0.45, now + i * 0.2 + 0.12);
              gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.2 + 0.18);
              osc.start(now + i * 0.2);
              osc.stop(now + i * 0.2 + 0.19);
            });
          }).catch(() => {});
        } catch {}
      }
      return newState;
    });
  }, []);

  // Trade toast notification - shows every trade with sound
  const showTradeToast = useCallback((tradeData, source = 'update') => {
    console.log('[TRADE_TOAST] Received trade:', tradeData, 'from source:', source);
    
    const amount = tradeData.amount || tradeData.btc_amount || 0;
    const pair = tradeData.pair || 'BTC-USD';
    const coin = pair.split('-')[0];
    const action = tradeData.action || tradeData.type || 'TRADE';
    const price = tradeData.price || 0;
    
    console.log(`[TRADE_TOAST] Showing toast for ${action} ${coin}, will play ${action === 'BUY' ? 'buy' : 'sell'} sound`);
    
    // Show the toast - use formatPrice for proper display of small prices like SHIB
    toast.success(
      `${action} ${amount.toFixed(6)} ${coin} @ ${formatPrice(price)}`,
      {
        description: `Value: $${(tradeData.usd_value || 0).toFixed(2)}${tradeData.profit ? ` | Profit: $${tradeData.profit.toFixed(4)}` : ''}`,
        duration: 4000,
      }
    );
    
    // Play audio alert for every trade
    playAlertSound(action === 'BUY' ? 'buy' : 'sell');
  }, [playAlertSound]);

  // Check for profit milestones and play alert
  const checkMilestones = useCallback((sessionProfit) => {
    const milestoneAmount = 500; // $500 milestones
    const currentMilestone = Math.floor(sessionProfit / milestoneAmount);
    
    if (currentMilestone > lastMilestone && currentMilestone > 0) {
      const profitAmount = currentMilestone * milestoneAmount;
      toast.success(`💰 Milestone! $${profitAmount} profit!`, { duration: 10000 });
      playAlertSound('milestone');
      setLastMilestone(currentMilestone);
    }
  }, [lastMilestone, playAlertSound]);

  const fetchPriceHistory = useCallback(async () => {
    try {
      // Reduce data points for faster load — chart still looks good with fewer points
      const limit = Math.min(Math.floor(chartTimeRange / 2), 200); // Cap at 200 points
      
      // Fetch history for all pairs — batch in groups of 4 to reduce server load
      const pairs = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'];
      const setters = [setPriceHistory, setEthPriceHistory, setSolPriceHistory, setLtcPriceHistory, setDogePriceHistory, setBchPriceHistory, setXrpPriceHistory, setShibPriceHistory];
      
      const formatHistory = (data) => (data?.history || []).map((item, idx) => ({
        ...item,
        time: new Date(item.timestamp).toLocaleTimeString('en-US', { hour12: false }),
        idx,
      }));

      // First batch: BTC + ETH (most important)
      const batch1 = await Promise.all(
        pairs.slice(0, 2).map(p => axios.get(`${API_V2}/market/history?limit=${limit}&minutes=${chartTimeRange}&pair=${p}`).catch(() => ({ data: { history: [] } })))
      );
      batch1.forEach((res, i) => setters[i](formatHistory(res.data)));

      // Second batch: remaining coins (slight delay)
      const batch2 = await Promise.all(
        pairs.slice(2).map(p => axios.get(`${API_V2}/market/history?limit=${limit}&minutes=${chartTimeRange}&pair=${p}`).catch(() => ({ data: { history: [] } })))
      );
      batch2.forEach((res, i) => setters[i + 2](formatHistory(res.data)));
    } catch (error) {
      console.error('Failed to fetch price history:', error);
    }
  }, [chartTimeRange]);

  const fetchTrades = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_V2}/user/trades?limit=100`, { withCredentials: true });
      const tradesList = response.data.trades || response.data || [];
      setTrades(tradesList);
      setLastTradeRefresh(new Date());
    } catch (error) {
      console.error('[fetchTrades] Failed:', error.response?.status, error.response?.data || error.message);
    }
  }, [user]);

  const fetchCoinbaseBalance = useCallback(async () => {
    try {
      console.log('[LIVE MODE] Fetching Coinbase balance...');
      const response = await axios.get(`${API_V2}/coinbase/accounts`, { withCredentials: true });
      console.log('[LIVE MODE] Coinbase accounts response:', response.data);
      
      if (response.data.success) {
        console.log('[LIVE MODE] Setting coinbaseBalance with', response.data.accounts?.length, 'accounts');
        console.log('[LIVE MODE] Data source:', response.data.source);
        setCoinbaseBalance(response.data);
        setLiveDataSource(response.data.source || 'exchange');
      } else {
        console.warn('[LIVE MODE] Coinbase response not successful:', response.data.error);
        setLiveDataSource('error: ' + (response.data.error || 'unknown'));
      }
    } catch (error) {
      console.error('[LIVE MODE] Failed to fetch Coinbase balance:', error);
      setLiveDataSource('error: fetch failed');
    }
  }, []);

  const fetchLifetimeProfit = useCallback(async () => {
    if (!user) return; // Require authentication
    try {
      const response = await axios.get(`${API_V2}/user/stats/lifetime-profit`, { withCredentials: true });
      setLifetimeProfit(response.data.total_profit || response.data.lifetime_profit);
      // Parse and format the reset date
      if (response.data.reset_date) {
        const date = new Date(response.data.reset_date);
        const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        setLifetimeProfitResetDate(formatted);
      }
    } catch (error) {
      console.error('Failed to fetch lifetime profit:', error);
    }
  }, [user]);

  const fetchActiveUsersCount = useCallback(async () => {
    // v3.9.30: retry with backoff — production URL sometimes 502s on cold start,
    // leaving the badge stuck at 0. This makes the count self-heal.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.get(`${API_V2}/auth/users`, { withCredentials: true });
        const users = response.data.users || response.data || [];
        setActiveUsersCount(users.length);
        return;
      } catch (error) {
        if (attempt === 2) {
          console.error('Failed to fetch active users count (all retries exhausted):', error);
          // Only set to 0 on final failure so the UI shows *something*
          setActiveUsersCount((prev) => (prev == null ? 0 : prev));
          return;
        }
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
  }, []);

  // Fetch system messages and admin status
  const fetchSystemMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_V2}/system/messages`, { withCredentials: true });
      setSystemMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch system messages:', error);
    }
  }, []);

  const checkAdminStatus = useCallback(async () => {
    if (!user) {
      console.log('[ADMIN CHECK] No user, skipping');
      return;
    }
    try {
      console.log('[ADMIN CHECK] Checking admin status for user:', user.user_id);
      const response = await axios.get(`${API_V2}/system/admin/check`, { withCredentials: true });
      console.log('[ADMIN CHECK] Response:', response.data);
      const isAdminResult = response.data.is_admin || false;
      console.log('[ADMIN CHECK] Setting isAdmin to:', isAdminResult);
      setIsAdmin(isAdminResult);
    } catch (error) {
      console.error('[ADMIN CHECK] Failed to check admin status:', error);
      setIsAdmin(false);
    }
  }, [user]);

  const fetchUnreadMessages = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API_V2}/messages/unread/count`, { withCredentials: true });
      setUnreadMessages(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread messages:', error);
    }
  }, [user]);

  const handleResetLifetimeProfit = async () => {
    if (!user) return; // Require authentication
    try {
      const response = await axios.post(`${API_V2}/user/stats/lifetime-profit/reset`, {}, { withCredentials: true });
      setLifetimeProfit(0);
      // Parse and format the new reset date
      if (response.data.reset_date) {
        const date = new Date(response.data.reset_date);
        const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        setLifetimeProfitResetDate(formatted);
      }
      toast.success('Lifetime profit counter reset');
    } catch (error) {
      toast.error('Failed to reset lifetime profit');
    }
  };

  const handleResetSessionBalance = async () => {
    if (!user) return;
    try {
      const response = await axios.post(`${API_V2}/user/bot/reset-session-balance`, {}, { withCredentials: true });
      // Reset the runTimer state
      setRunTimer(prev => ({
        ...prev,
        startTime: Date.now(),
        startingBalance: response.data.new_start_balance,
        currentProfit: 0,
        profitPerHour: 0,
        elapsedSeconds: 0,
      }));
      toast.success(`Session P/L reset. New start balance: $${response.data.new_start_balance.toFixed(2)}`);
    } catch (error) {
      toast.error('Failed to reset session balance');
    }
  };

  const fetchLifetimeSkim = useCallback(async () => {
    try {
      // Use v2 endpoint for skim stats
      const response = await axios.get(`${API_V2}/skim/stats`, { withCredentials: true });
      setLifetimeSkim(response.data.lifetime_skim || 0);
      // Also update skimData with the user's configured skim amounts
      setSkimData(prev => ({
        ...prev,
        skim_trigger_amount: response.data.skim_trigger_amount || 500,
        skim_transfer_amount: response.data.skim_transfer_amount || 5
      }));
    } catch (error) {
      console.error('Failed to fetch lifetime skim:', error);
    }
  }, []);

  // WebSocket connection - uses user-specific WebSocket when authenticated
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    // Don't connect if user is not authenticated
    if (!user) {
      console.log('WebSocket: Skipping connection, user not authenticated');
      return;
    }

    // Use user-specific WebSocket only
    const wsUrl = `${WS_URL}/ws/user/${user.user_id}`;
    const ws = new WebSocket(wsUrl);
    let pingInterval = null;

    ws.onopen = () => {
      setIsConnected(true);
      console.log('WebSocket connected (user:', user.user_id, ')');
      
      // Send ping every 15 seconds to keep connection alive through proxies/load balancers
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 15000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle ping/pong messages (keep-alive)
        if (data.type === 'ping' || data.type === 'pong') {
          return;
        }

        // Handle trade_notification - browser push + in-app
        if (data.type === 'trade_notification') {
          if ('Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification(data.title, { body: data.body, icon: '/logo.png', tag: `trade-${Date.now()}` });
            } catch (e) { /* silent */ }
          }
          return;
        }

        // v3.9.30: celebration toast when a skim milestone event fires
        if (data.type === 'skim_milestone') {
          const paid = data.milestones_paid || 0;
          const total = Number(data.total_transfer || 0).toFixed(2);
          const cur = data.currency || 'BTC';
          const ms = paid > 1 ? 'milestones' : 'milestone';
          const wallet = data.wallet_display || 'your cold wallet';
          const modeTag = (data.mode || '').toUpperCase() === 'LIVE' ? 'LIVE' : 'SIM';
          const liveOk = data.live_transfer_success;
          const description = liveOk === false
            ? `⚠ live transfer failed — counter still updated. Check Admin → SKIM.`
            : `${paid} ${ms} paid · $${total} → ${wallet} (${cur})`;
          toast.success(`🎉 SKIM MILESTONE [${modeTag}]`, {
            description,
            duration: 6500,
            id: `skim-${data.timestamp || Date.now()}`,  // dedupe multi-tab
          });
          return;
        }

        // Handle reset message - clear all state
        if (data.type === 'reset') {
          if (data.portfolio) setPortfolio(data.portfolio);
          if (data.pairs) setPairsData(data.pairs);
          if (data.skim) setSkimData(data.skim);
          // Clear price histories
          setPriceHistory([]);
          setEthPriceHistory([]);
          setSolPriceHistory([]);
          // Reset other state
          setCurrentPrice(0);
          setRsi(50);
          setSignal('HOLD');
          toast.success('Portfolio reset complete', { duration: 3000 });
          // Refresh data
          fetchTrades();
          fetchLifetimeProfit();
          fetchLifetimeSkim();
          return;
        }

        if (data.type === 'init' || data.type === 'update') {
          setCurrentPrice(data.price);
          setRsi(data.rsi);
          if (data.signal) setSignal(data.signal);
          if (data.status) setBotStatus(data.status);
          if (data.mode) setTradingMode(data.mode);
          if (data.portfolio) setPortfolio(data.portfolio);
          if (data.skim) {
            setSkimData(data.skim);
            // Check for profit milestones
            if (data.skim.session_profit) {
              checkMilestones(data.skim.session_profit);
            }
          }
          if (data.grid) setGridData(data.grid);
          
          // Update pairs data - preserve avg_buy_price to prevent flicker
          if (data.pairs) {
            setPairsData(prevPairs => {
              const newPairs = { ...data.pairs };
              Object.keys(newPairs).forEach(pair => {
                if (!newPairs[pair].high_guard && prevPairs[pair]?.high_guard) newPairs[pair].high_guard = prevPairs[pair].high_guard;
                const oldAvg = prevPairs[pair]?.avg_buy_price || 0;
                const newAvg = newPairs[pair].avg_buy_price || 0;
                
                if (oldAvg > 0) {
                  if (newAvg === 0) {
                    newPairs[pair].avg_buy_price = oldAvg;
                  } else if (Math.abs(newAvg - oldAvg) / oldAvg < 0.01) {
                    newPairs[pair].avg_buy_price = oldAvg;
                  }
                }
              });
              return newPairs;
            });
          }

          // Update price history for all pairs from WebSocket data
          if (data.pairs) {
            const timestamp = new Date(data.timestamp).toLocaleTimeString('en-US', { hour12: false });
            
            if (data.pairs['BTC-USD']) {
              setPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['BTC-USD'].price,
                  rsi: data.pairs['BTC-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['ETH-USD']) {
              setEthPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['ETH-USD'].price,
                  rsi: data.pairs['ETH-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['SOL-USD']) {
              setSolPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['SOL-USD'].price,
                  rsi: data.pairs['SOL-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['LTC-USD']) {
              setLtcPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['LTC-USD'].price,
                  rsi: data.pairs['LTC-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['DOGE-USD']) {
              setDogePriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['DOGE-USD'].price,
                  rsi: data.pairs['DOGE-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['BCH-USD']) {
              setBchPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['BCH-USD'].price,
                  rsi: data.pairs['BCH-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['XRP-USD']) {
              setXrpPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['XRP-USD'].price,
                  rsi: data.pairs['XRP-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
            
            if (data.pairs['SHIB-USD']) {
              setShibPriceHistory((prev) => {
                const newPoint = {
                  price: data.pairs['SHIB-USD'].price,
                  rsi: data.pairs['SHIB-USD'].rsi,
                  time: timestamp,
                  idx: prev.length,
                };
                return [...prev, newPoint].slice(-50);
              });
            }
          }

          // Show trade notification (rate-limited to prevent spam)
          if (data.trade) {
            if (showTradeToastRef.current) showTradeToastRef.current(data.trade, 'update');
            // Small delay to ensure trade is committed to DB before fetching
            setTimeout(() => { if (fetchTradesRef.current) fetchTradesRef.current(); }, 500);
          }
        }
        
        // Handle trade messages (from user-specific broadcasts)
        // These come with type: 'trade' and may include pairs data
        if (data.type === 'trade' && data.trade) {
          if (showTradeToastRef.current) showTradeToastRef.current(data.trade, 'trade');
          // Update portfolio and pairs if included
          if (data.portfolio) setPortfolio(data.portfolio);
          // Update pairs - preserve avg_buy_price to prevent flicker
          if (data.pairs) {
            setPairsData(prevPairs => {
              const newPairs = { ...data.pairs };
              Object.keys(newPairs).forEach(pair => {
                if (!newPairs[pair].high_guard && prevPairs[pair]?.high_guard) newPairs[pair].high_guard = prevPairs[pair].high_guard;
                const oldAvg = prevPairs[pair]?.avg_buy_price || 0;
                const newAvg = newPairs[pair].avg_buy_price || 0;
                
                if (oldAvg > 0) {
                  if (newAvg === 0) {
                    newPairs[pair].avg_buy_price = oldAvg;
                  } else if (Math.abs(newAvg - oldAvg) / oldAvg < 0.01) {
                    newPairs[pair].avg_buy_price = oldAvg;
                  }
                }
              });
              return newPairs;
            });
          }
          // Small delay to ensure trade is committed to DB before fetching
          setTimeout(() => { if (fetchTradesRef.current) fetchTradesRef.current(); }, 500);
        }

        // Handle bot stopped notification
        if (data.type === 'bot_stopped') {
          console.log('[BOT STOPPED]', data);
          toast.error('Bot Stopped', {
            description: `Trading loop exited after ${data.iterations} iterations. Reason: ${data.reason}`,
            duration: 10000,
          });
          // Refresh status to update UI
          fetchBotStatus();
        }

        // Handle error messages from backend
        if (data.type === 'error') {
          toast.error('Trading Error', {
            description: data.error,
            duration: 8000,
          });
        }
      } catch (error) {
        // Might be a plain text response like "pong"
        if (event.data === 'pong') return;
        console.error('WebSocket message error:', error);
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      console.log('WebSocket disconnected, code:', event.code, 'reason:', event.reason);
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      // Reconnect after 2 seconds using ref to avoid circular dependency
      // Only reconnect if not a clean close (code 1000) and user is still logged in
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          if (connectWebSocketRef.current) {
            console.log('Attempting WebSocket reconnection...');
            connectWebSocketRef.current();
          }
        }, 2000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
    };

    wsRef.current = ws;
  }, [user, fetchTrades, playAlertSound, checkMilestones, fetchLifetimeProfit, fetchLifetimeSkim]);
  
  // Keep ref updated with latest connectWebSocket function
  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  // Keep ref updated with latest fetchTrades function
  useEffect(() => {
    fetchTradesRef.current = fetchTrades;
  }, [fetchTrades]);

  // Keep ref updated with latest showTradeToast function
  useEffect(() => {
    showTradeToastRef.current = showTradeToast;
  }, [showTradeToast]);

  // Stable trades polling - separate from the main effect so it doesn't reset
  useEffect(() => {
    if (authLoading || !user) return;
    const tradesInterval = setInterval(() => {
      if (fetchTradesRef.current) fetchTradesRef.current();
    }, 15000);
    // Tick the "updated Xs ago" counter every second
    const tickInterval = setInterval(() => setTickCounter(c => c + 1), 1000);
    return () => { clearInterval(tradesInterval); clearInterval(tickInterval); };
  }, [authLoading, user]);

  // Initial data fetch and WebSocket connection - WAIT FOR AUTH TO COMPLETE
  useEffect(() => {
    // Don't fetch data until auth check is complete
    if (authLoading) {
      return;
    }
    
    // Fetch active users count (public endpoint, doesn't require auth)
    fetchActiveUsersCount();
    
    // If user is required but not logged in, don't fetch user-specific data
    if (!user) {
      console.log('User not authenticated, skipping data fetch');
      return;
    }
    
    console.log('Auth complete, fetching user data for:', user.user_id);
    
    // CRITICAL: Load bot status + WebSocket first (shows prices immediately)
    fetchBotStatus();
    connectWebSocket();
    
    // STAGGER secondary loads to avoid overloading
    const t1 = setTimeout(() => { fetchTrades(); fetchLifetimeProfit(); }, 500);
    const t2 = setTimeout(() => { fetchPriceHistory(); }, 1500);
    const t3 = setTimeout(() => { fetchLifetimeSkim(); fetchSystemMessages(); checkAdminStatus(); fetchUnreadMessages(); }, 3000);

    // REDUCED polling intervals - WebSocket handles real-time updates
    const statusInterval = setInterval(fetchBotStatus, 30000);
    const tradesInterval = setInterval(fetchTrades, 15000);
    const profitInterval = setInterval(fetchLifetimeProfit, 30000);
    const skimInterval = setInterval(fetchLifetimeSkim, 60000);
    const priceHistoryInterval = setInterval(fetchPriceHistory, 60000);
    const usersInterval = setInterval(fetchActiveUsersCount, 60000);
    const messagesInterval = setInterval(fetchSystemMessages, 120000);
    const unreadInterval = setInterval(fetchUnreadMessages, 60000);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearInterval(statusInterval);
      clearInterval(tradesInterval);
      clearInterval(profitInterval);
      clearInterval(skimInterval);
      clearInterval(priceHistoryInterval);
      clearInterval(usersInterval);
      clearInterval(messagesInterval);
      clearInterval(unreadInterval);
    };
  }, [authLoading, user, fetchBotStatus, fetchPriceHistory, fetchTrades, fetchLifetimeProfit, fetchLifetimeSkim, fetchActiveUsersCount, fetchSystemMessages, checkAdminStatus, fetchUnreadMessages, connectWebSocket]);

  // Immediately fetch price history when time range changes
  useEffect(() => {
    fetchPriceHistory();
  }, [chartTimeRange, fetchPriceHistory]);

  // Fetch Coinbase balance when in LIVE mode - always try, don't wait for coinbaseConnected
  useEffect(() => {
    if (tradingMode === 'live') {
      console.log('[LIVE MODE] Mode is LIVE - initiating balance fetch');
      // Immediate fetch
      fetchCoinbaseBalance();
      // Then refresh every 5 seconds for more responsive updates
      const interval = setInterval(() => {
        console.log('[LIVE MODE] Auto-refresh balance...');
        fetchCoinbaseBalance();
      }, 5000);
      return () => clearInterval(interval);
    } else {
      // Clear live data when switching to simulation
      setLiveDataSource(null);
      setCoinbaseBalance(null);
    }
  }, [tradingMode, fetchCoinbaseBalance]);

  // Run timer effect - uses server-persisted start time
  useEffect(() => {
    if (botStatus === 'running' && runTimer.startTime) {
      // Update timer every second using server's start time
      const intervalId = setInterval(() => {
        setRunTimer(prev => {
          if (!prev.startTime) return prev;
          
          const elapsed = Math.floor((Date.now() - prev.startTime) / 1000);
          const currentProfit = portfolio.total_value - prev.startingBalance;
          const hours = elapsed / 3600;
          const profitPerHour = hours > 0 ? currentProfit / hours : 0;
          
          return {
            ...prev,
            elapsedSeconds: elapsed,
            currentProfit,
            profitPerHour,
          };
        });
      }, 1000);

      return () => clearInterval(intervalId);
    }
    // Don't reset startTime here - let the backend control it
  }, [botStatus, portfolio.total_value, runTimer.startTime, runTimer.startingBalance]);

  // RSI Stabilization countdown effect - runs the timer independently once started
  useEffect(() => {
    // Only run if countdown is active
    if (!rsiCountdown.isActive || rsiCountdown.secondsRemaining <= 0) {
      return;
    }
    
    const intervalId = setInterval(() => {
      setRsiCountdown(prev => {
        if (prev.secondsRemaining <= 1) {
          // Mark as completed so it won't restart
          return { ...prev, isActive: false, secondsRemaining: 0, hasCompleted: true };
        }
        return { ...prev, secondsRemaining: prev.secondsRemaining - 1 };
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [rsiCountdown.isActive]); // Only depend on isActive, not sessionStartTime

  // Persist countdown state to localStorage when it changes
  useEffect(() => {
    if (rsiCountdown.hasCompleted && rsiCountdown.sessionStartTime) {
      try {
        localStorage.setItem('rsiCountdownState', JSON.stringify({
          sessionStartTime: rsiCountdown.sessionStartTime,
          hasCompleted: true,
        }));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [rsiCountdown.hasCompleted, rsiCountdown.sessionStartTime]);

  // Reset RSI countdown when bot explicitly stops (user clicks STOP)
  useEffect(() => {
    // Only reset if bot is stopped AND countdown is not active
    // This prevents interrupting an ongoing countdown
    if (botStatus === 'stopped') {
      setRsiCountdown(prev => {
        // Never interrupt an active countdown
        if (prev.isActive && prev.secondsRemaining > 0) {
          return prev;
        }
        // Reset completely for next session when bot stops
        if (prev.sessionStartTime !== null || prev.hasCompleted) {
          // Also clear localStorage
          try {
            localStorage.removeItem('rsiCountdownState');
          } catch (e) {}
          return { isActive: false, secondsRemaining: 240, sessionStartTime: null, hasCompleted: false };
        }
        return prev;
      });
    }
  }, [botStatus]);

  // Reset timer display when portfolio is fully reset (trades = 0 and backend says stopped)
  useEffect(() => {
    if (portfolio.total_trades === 0 && botStatus === 'stopped') {
      setRunTimer({
        startTime: null,
        elapsedSeconds: 0,
        startingBalance: 0,
        currentProfit: 0,
        profitPerHour: 0,
        isLoaded: true,  // Keep isLoaded true so we don't show loading state
      });
    }
  }, [portfolio.total_trades, botStatus]);

  // Get display portfolio - use Coinbase balance in LIVE mode
  const displayPortfolio = useMemo(() => {
    console.log('[DISPLAY] tradingMode:', tradingMode, 'coinbaseBalance?.success:', coinbaseBalance?.success);
    
    if (tradingMode === 'live' && coinbaseBalance?.success) {
      // Parse all balances from coinbaseBalance.accounts
      const accounts = coinbaseBalance.accounts || [];
      console.log('[DISPLAY] Parsing', accounts.length, 'Coinbase accounts');
      
      const balances = {
        usd_balance: 0,
        btc_balance: 0,
        eth_balance: 0,
        sol_balance: 0,
        ltc_balance: 0,
        doge_balance: 0,
        bch_balance: 0,
        xrp_balance: 0,
        shib_balance: 0,
        dgb_balance: 0,
      };
      
      // Map Coinbase accounts to our balance format
      accounts.forEach(acc => {
        const currency = (acc.currency || '').toUpperCase();
        const available = parseFloat(acc.available) || 0;
        
        if (currency === 'USD' || currency === 'USDT') {
          balances.usd_balance = available;
        } else if (currency === 'BTC') {
          balances.btc_balance = available;
        } else if (currency === 'ETH') {
          balances.eth_balance = available;
        } else if (currency === 'SOL') {
          balances.sol_balance = available;
        } else if (currency === 'LTC') {
          balances.ltc_balance = available;
        } else if (currency === 'DOGE') {
          balances.doge_balance = available;
        } else if (currency === 'BCH') {
          balances.bch_balance = available;
        } else if (currency === 'XRP') {
          balances.xrp_balance = available;
        } else if (currency === 'SHIB') {
          balances.shib_balance = available;
        }
      });
      
      console.log('[DISPLAY] Parsed LIVE balances:', balances);
      
      return {
        ...balances,
        total_value: coinbaseBalance.total_value || 0,
        avg_buy_price: portfolio.avg_buy_price || 0,
        pnl: portfolio.pnl || 0,
        pnl_percent: portfolio.pnl_percent || 0,
        realized_pnl: portfolio.realized_pnl || 0,
        unrealized_pnl: portfolio.unrealized_pnl || 0,
        total_trades: portfolio.total_trades,
        winning_trades: portfolio.winning_trades,
        win_rate: portfolio.win_rate,
      };
    }
    return portfolio;
  }, [tradingMode, coinbaseBalance, portfolio]);

  // Bot control handlers - REQUIRE user authentication, use v2 API
  // Bot control handlers (extracted to dashboard/botControls.js)
  const botControls = createBotControls({
    user,
    tradingMode,
    botStatus,
    setBotStatus,
    setShowStopConfirm,
    setTradingMode,
    setDumpConfirm,
    setBleedPairs,
    setBleedLoading,
    setTrades,
    setPriceHistory,
    countdownMusicRef,
    musicPlayingRef,
    fetchBotStatus,
    fetchTrades,
    playAlertSound,
  });
  const {
    handleStartBot, handleStopBot, confirmStopBot, handlePauseBot,
    handleModeChange, handleResetPortfolio, handleSellHalf, handleDumpCoin,
    handleToggleBleed, handleClearTradeHistory, handleExportCSV, handleExportJSON, handleShare,
  } = botControls;

  // Chart tooltip - uses extracted component
  const CustomTooltip = ChartTooltip;

  // RSI helpers - delegate to extracted functions
  const rsiColor = getRsiColor(rsi, settings.rsi_oversold, settings.rsi_overbought);
  const rsiStatus = getRsiStatus(rsi, settings.rsi_oversold, settings.rsi_overbought);

  // Get the selected chart's enhanced data - delegates to extracted helper
  const getSelectedChartData = () => {
    const priceHistories = {
      'BTC-USD': priceHistory,
      'ETH-USD': ethPriceHistory,
      'SOL-USD': solPriceHistory,
      'LTC-USD': ltcPriceHistory,
      'DOGE-USD': dogePriceHistory,
      'BCH-USD': bchPriceHistory,
      'XRP-USD': xrpPriceHistory,
      'SHIB-USD': shibPriceHistory,
    };
    return getSelectedChartDataHelper(selectedChart, priceHistories);
  };

  return (
    <div className="min-h-screen text-[#00FF41] font-mono" style={{ background: 'transparent' }}>
      <Toaster
        theme="dark"
        position="top-right"
        visibleToasts={3}
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid #003B00',
            color: '#00FF41',
          },
          duration: 4000,
        }}
      />

      {/* RSI Stabilization Countdown Splash Screen */}
      <RSICountdown 
        isActive={rsiCountdown.isActive} 
        secondsRemaining={rsiCountdown.secondsRemaining} 
      />

      {/* Critical System Message Modal */}
      <SystemMessageModal messages={systemMessages} />

      {/* Stop Bot Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="stop-confirm-modal">
          <div className="bg-[#0a0a0a] border-2 border-red-600 p-6 rounded-lg max-w-md w-full mx-4">
            <div className="text-center">
              <div className="text-red-500 text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-red-500 mb-2 uppercase tracking-wider">Stop Trading Bot?</h2>
              <p className="text-[#008F11] mb-6">
                Are you sure you want to stop the bot? All active trading will cease immediately.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => setShowStopConfirm(false)}
                  className="bg-[#003B00] hover:bg-[#004d00] text-[#00FF41] border border-[#00FF41] px-6"
                  data-testid="stop-confirm-no"
                >
                  NO - KEEP RUNNING
                </Button>
                <Button
                  onClick={confirmStopBot}
                  className="bg-red-600 hover:bg-red-700 text-white px-6"
                  data-testid="stop-confirm-yes"
                >
                  YES - STOP BOT
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <DashboardHeader
        user={user}
        tradingMode={tradingMode}
        isConnected={isConnected}
        coinbaseConnected={coinbaseConnected}
        botStatus={botStatus}
        activeUsersCount={activeUsersCount}
        lifetimeProfit={lifetimeProfit}
        lifetimeProfitResetDate={lifetimeProfitResetDate}
        runTimer={runTimer}
        isAdmin={isAdmin}
        accessLevel={accessLevel}
        unreadMessages={unreadMessages}
        activeExchange={activeExchange}
        onModeChange={handleModeChange}
        onResetLifetimeProfit={handleResetLifetimeProfit}
        onResetSessionBalance={handleResetSessionBalance}
        onClearTradeHistory={handleClearTradeHistory}
        onShare={handleShare}
        onLogout={logout}
        onManualTrade={() => setManualTradeOpen(true)}
      />

      {/* Main Content */}
      <main className="p-4 max-w-[1920px] mx-auto">
        {/* System Messages Banner */}
        <SystemMessageBanner messages={systemMessages} />
        
        {/* Live Warning Banner */}
        {tradingMode === 'live' && (
          <div className="mb-4 p-3 bg-red-950/30 border border-red-600 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" strokeWidth={1.5} />
              <span className="text-red-500 text-sm uppercase tracking-wider">
                WARNING: LIVE TRADING MODE ACTIVE - REAL FUNDS AT RISK
              </span>
            </div>
            <div className="flex items-center gap-2">
              {coinbaseBalance?.success ? (
                <span className="text-green-400 text-xs bg-green-900/30 px-2 py-1 rounded">
                  ✓ DATA: {liveDataSource?.toUpperCase() || 'LIVE'}
                </span>
              ) : (
                <span className="text-yellow-400 text-xs bg-yellow-900/30 px-2 py-1 rounded animate-pulse">
                  ⟳ {liveDataSource || 'FETCHING DATA...'}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats Row */}
        <StatsRow
          currentPrice={currentPrice}
          rsi={rsi}
          portfolio={displayPortfolio}
          signal={signal}
          tradingMode={tradingMode}
          rsiColor={rsiColor}
          rsiStatus={rsiStatus}
        />

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Chart Section */}
          <div className="lg:col-span-8">
            <Card className="matrix-card rounded-none" data-testid="chart-card">
              <CardHeader className="border-b border-[#003B00] p-4">
                <CardTitle className="text-sm uppercase tracking-widest flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" strokeWidth={1.5} />
                      {selectedChart} PRICE CHART
                    </div>
                    {/* Time Range Selector */}
                    <div className="flex gap-1" data-testid="chart-time-selector">
                      {[
                        { label: '1H', value: 60 },
                        { label: '4H', value: 240 },
                        { label: '12H', value: 720 },
                        { label: '24H', value: 1440 },
                        { label: '7D', value: 10080 },
                        { label: '30D', value: 43200 },
                      ].map(({ label, value }) => (
                        <button
                          key={value}
                          onClick={() => setChartTimeRange(value)}
                          className={`px-2 py-1 text-xs font-mono transition-colors ${
                            chartTimeRange === value
                              ? 'bg-[#FFD700] text-black'
                              : 'bg-[#001a00] text-[#008F11] hover:bg-[#003B00] hover:text-[#00FF41]'
                          }`}
                          data-testid={`time-range-${label}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Coin Selector */}
                  <div className="flex gap-1 flex-wrap">
                    {['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'].map((pair) => (
                      <button
                        key={pair}
                        onClick={() => setSelectedChart(pair)}
                        className={`px-2 py-1 text-xs font-mono transition-colors ${
                          selectedChart === pair
                            ? 'bg-[#00FF41] text-black'
                            : 'bg-[#001a00] text-[#00FF41] hover:bg-[#003B00]'
                        }`}
                      >
                        {pair.split('-')[0]}
                      </button>
                    ))}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Professional Candlestick Chart */}
                <CandlestickChart
                  data={getSelectedChartData()}
                  avgBuyPrice={pairsData[selectedChart]?.avg_buy_price || 0}
                  height={350}
                  selectedPair={selectedChart}
                  duration={chartTimeRange}
                />

                {/* RSI Chart */}
                <div className="mt-4 h-[100px] border-t border-[#003300] pt-2">
                  <div className="text-xs text-gray-500 mb-1 flex justify-between items-center px-1">
                    <span>RSI INDICATOR</span>
                    <span className={`font-mono ${rsi <= settings.rsi_oversold ? 'text-green-400' : rsi >= settings.rsi_overbought ? 'text-red-400' : 'text-yellow-400'}`}>
                      {rsi?.toFixed(1) || '--'}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height="85%">
                    <AreaChart 
                      data={getSelectedChartData()} 
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="rsiGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#FFD700" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#FFD700" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="1 4" stroke="#1a2a1a" vertical={false} />
                      <XAxis dataKey="time" stroke="#003300" tick={{ fontSize: 9, fill: '#004400' }} hide />
                      <YAxis 
                        stroke="#003300" 
                        tick={{ fontSize: 9, fill: '#006600' }} 
                        domain={[0, 100]} 
                        ticks={[30, 50, 70]}
                        width={30}
                      />
                      {/* Oversold zone (green) */}
                      <ReferenceArea y1={0} y2={settings.rsi_oversold} fill="#00FF41" fillOpacity={0.1} />
                      {/* Overbought zone (red) */}
                      <ReferenceArea y1={settings.rsi_overbought} y2={100} fill="#FF0000" fillOpacity={0.1} />
                      <ReferenceLine y={settings.rsi_oversold} stroke="#00FF41" strokeDasharray="3 3" strokeOpacity={0.6} />
                      <ReferenceLine y={50} stroke="#444" strokeDasharray="2 4" strokeOpacity={0.4} />
                      <ReferenceLine y={settings.rsi_overbought} stroke="#FF0000" strokeDasharray="3 3" strokeOpacity={0.6} />
                      <Area 
                        type="monotone" 
                        dataKey="rsi" 
                        stroke="#FFD700" 
                        strokeWidth={1.5} 
                        fill="url(#rsiGradient)"
                        dot={false} 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Grid Trading Card */}
            <GridTradingCard gridData={gridData} currentPrice={currentPrice} />

            {/* Multi-Coin Status Card */}
            <MultiCoinStatus
              pairsData={pairsData}
              bleedPairs={bleedPairs}
              bleedLoading={bleedLoading}
              dumpConfirm={dumpConfirm}
              setDumpConfirm={setDumpConfirm}
              formatPrice={formatPrice}
              handleToggleBleed={handleToggleBleed}
              handleDumpCoin={handleDumpCoin}
              setActiveTab={setActiveTab}
            />
          </div>

          {/* Controls Section */}
          <div className="lg:col-span-4 space-y-4">
            {/* Bot Controls */}
            <BotControlsCard
              botStatus={botStatus}
              tradingMode={tradingMode}
              audioAlertsEnabled={audioAlertsEnabled}
              handleStartBot={handleStartBot}
              handlePauseBot={handlePauseBot}
              handleStopBot={handleStopBot}
              handleResetPortfolio={handleResetPortfolio}
              toggleAudioAlerts={toggleAudioAlerts}
            />

            {/* Portfolio Details */}
            <PortfolioDetails
              tradingMode={tradingMode}
              displayPortfolio={displayPortfolio}
              coinbaseBalance={coinbaseBalance}
              liveDataSource={liveDataSource}
              syncing={syncing}
              fullSyncing={fullSyncing}
              fullSyncStatus={fullSyncStatus}
              syncWithExchange={syncWithExchange}
              startFullHistorySync={startFullHistorySync}
              handleOpenCostBasis={handleOpenCostBasis}
              fetchTrades={fetchTrades}
            />

            {/* RSI Settings */}
            <SettingsDisplay
              settings={settings}
              fetchBotStatus={fetchBotStatus}
              setSettingsOpen={setSettingsOpen}
            />
          </div>
        </div>

        {/* Live Trade Feed */}
        <LiveTradeFeed mode={tradingMode} />

        {/* Protection Sell Audit (v3.9.6) */}
        <ProtectionSellAudit mode={tradingMode} />

        {/* Multi-Coin Chart - Always Visible */}
        <div className="mt-4" data-testid="multi-coin-section">
          <MultiCoinOverlay 
            height={300} 
            activePairs={settings.active_pairs || ['BTC-USD', 'ETH-USD', 'SOL-USD']} 
          />
        </div>

        {/* Trade History & Performance Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="flex w-full flex-wrap gap-1 bg-[#0a0a0a] border border-[#003B00] p-1 h-auto">
            <TabsTrigger 
              value="trading" 
              className="flex-1 min-w-[100px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-trading"
            >
              Trade History
            </TabsTrigger>
            <TabsTrigger 
              value="prices" 
              className="flex-1 min-w-[100px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-prices"
            >
              Price Compare
            </TabsTrigger>
            <TabsTrigger 
              value="arbitrage" 
              className="flex-1 min-w-[80px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-arbitrage"
            >
              Arbitrage
            </TabsTrigger>
            {/* Multi-Chart moved to always-visible section above */}
            <TabsTrigger 
              value="performance" 
              className="flex-1 min-w-[90px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-performance"
            >
              Performance
            </TabsTrigger>
            <TabsTrigger 
              value="coins" 
              className="flex-1 min-w-[60px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-coins"
            >
              Coins
            </TabsTrigger>
            <TabsTrigger 
              value="diagnostics" 
              className="flex-1 min-w-[90px] data-[state=active]:bg-[#003B00] data-[state=active]:text-[#00FF41] text-[#008F11] uppercase text-[10px] tracking-wider px-3 py-2"
              data-testid="tab-diagnostics"
            >
              Diagnostics
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="trading" className="mt-4">
            <TradeHistoryTab
              trades={trades}
              lastTradeRefresh={lastTradeRefresh}
              tickCounter={tickCounter}
              onRefresh={fetchTrades}
              onExportCSV={handleExportCSV}
              onExportJSON={handleExportJSON}
            />
          </TabsContent>
          
          <TabsContent value="prices" className="mt-4">
            <PriceCompare activePairs={settings.active_pairs || ['BTC-USD', 'ETH-USD', 'SOL-USD']} />
          </TabsContent>
          
          <TabsContent value="arbitrage" className="mt-4">
            <ArbitrageScanner 
              onAlert={(alerts) => {
                if (alerts.length > 0) {
                  toast.success(`${alerts.length} arbitrage opportunity found!`, {
                    description: `${alerts[0].pair}: ${alerts[0].spread_percent.toFixed(2)}% spread`,
                  });
                }
              }}
            />
          </TabsContent>

          {/* Multi-Chart is now always visible above tabs */}
          
          <TabsContent value="performance" className="mt-4">
            <PerformanceDashboard />
          </TabsContent>

          <TabsContent value="coins" className="mt-4">
            <Card className="matrix-card rounded-none" data-testid="coins-overview-card">
              <CardHeader className="border-b border-[#003B00] p-4">
                <CardTitle className="text-sm uppercase tracking-widest flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
                  MARKET OVERVIEW
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <CoinOverviewTable />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="diagnostics" className="mt-4">
            <BotDiagnostics />
          </TabsContent>
        </Tabs>

        {/* Pre-owned Assets Section */}
        <ManualBuyLotsSection />
      </main>

      {/* Footer */}
      <footer className="border-t border-[#003B00] p-4 mt-8">
        <div className="max-w-[1920px] mx-auto flex flex-col gap-3 text-xs text-[#008F11]">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span>BITBOT V3.9.32 [LIVE] | RSI STRATEGY + PROTECTION</span>
            <MiniPlayer />
            <span>
              {tradingMode === 'simulation' ? (
                'SIMULATION MODE - NO REAL FUNDS'
              ) : (
                <span className="text-red-500">LIVE MODE - CONNECTED TO COINBASE</span>
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 border-t border-[#003B00]/40">
            <a href="/about" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-about">About</a>
            <span className="text-[#003B00]">|</span>
            <a href="/faq" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-faq">FAQ</a>
            <span className="text-[#003B00]">|</span>
            <a href="/contact" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-contact">Contact</a>
            <span className="text-[#003B00]">|</span>
            <a href="/privacy" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-privacy">Privacy</a>
            <span className="text-[#003B00]">|</span>
            <a href="/terms" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-terms">Terms</a>
            <span className="text-[#003B00]">|</span>
            <a href="/disclaimer" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-disclaimer">Disclaimer</a>
            <span className="text-[#003B00]">|</span>
            <a href="/cookies" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-cookies">Cookies</a>
            <span className="text-[#003B00]">|</span>
            <a href="/changelog" className="hover:text-[#00FF41] transition-colors" data-testid="dash-footer-changelog">Changelog</a>
            {accessLevel === 'GOD' && (
              <>
                <span className="text-[#003B00]">|</span>
                <a href="/diag/sell-audit" className="hover:text-[#00FF41] transition-colors text-yellow-500" data-testid="dash-footer-diag">Diag</a>
              </>
            )}
          </div>
        </div>
      </footer>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        botStatus={botStatus}
        onSettingsUpdated={fetchBotStatus}
      />

      <ManualTradeModal
        open={manualTradeOpen}
        onOpenChange={setManualTradeOpen}
        onTradeComplete={fetchBotStatus}
      />
      
      {/* Set Average Buy Price Dialog */}
      <AlertDialog open={setAvgBuyDialog.open} onOpenChange={(open) => !open && setSetAvgBuyDialog({ open: false, pair: null, coinName: null, currentBalance: 0 })}>
        <AlertDialogContent className="bg-black border border-[#008F11]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#008F11]">
              Set Average Buy Price for {setAvgBuyDialog.coinName}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Enter the price you paid per coin. This is used to calculate profit/loss.
              <br />
              <span className="text-cyan-400">Your balance: {setAvgBuyDialog.currentBalance?.toLocaleString()} {setAvgBuyDialog.coinName}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2">
              <span className="text-[#008F11]">$</span>
              <Input
                type="number"
                step="any"
                placeholder="0.00000612"
                value={avgBuyInput}
                onChange={(e) => setAvgBuyInput(e.target.value)}
                className="bg-black border-[#008F11] text-white"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Tip: For SHIB, enter something like 0.000006
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#008F11] text-[#008F11] hover:bg-[#003B00]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSetAvgBuy}
              className="bg-[#008F11] text-black hover:bg-[#00FF00]"
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Cost Basis Debug Modal */}
      <CostBasisModal
        open={costBasisOpen}
        onOpenChange={setCostBasisOpen}
        playAlertSound={playAlertSound}
      />
    </div>
  );
};

export default Dashboard;
